import { Link, useLocation } from "@/lib/router-compat";
import { MapPin, Phone, Clock } from "lucide-react";
import kodiakWhiteLogo from "@/assets/kodiak-wordmark-white.png";
import skillMammothLogo from "@/assets/skill-mammoth-mammoth-white.png";

const services = [
  { name: "Roof Replacement", path: "/services/roof-replacement" },
  { name: "Roof Repair", path: "/services/roof-repair" },
  { name: "Siding", path: "/services/siding" },
  { name: "Windows", path: "/services/windows" },
  { name: "Gutters", path: "/services/gutters" },
];

const quickLinks = [
  { name: "Our Team", path: "/about" },
  { name: "Careers", path: "/careers" },
  // { name: "Testimonials", path: "/testimonials" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

const locations = [
  { name: "Blaine", path: "/locations/blaine" },
  { name: "Coon Rapids", path: "/locations/coon-rapids" },
  { name: "Eden Prairie", path: "/locations/eden-prairie" },
  { name: "Plymouth", path: "/locations/plymouth" },
  { name: "St. Paul", path: "/locations/st-paul" },
  { name: "Woodbury", path: "/locations/woodbury" },
];

export const Footer = () => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const skillMammothRel = isHomePage ? "noopener noreferrer" : "nofollow noopener noreferrer";

  return (
    <footer className="bg-footer text-footer-foreground" role="contentinfo">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Logo & About */}
          <div className="space-y-6">
            <Link to="/" aria-label="Kodiak Structures & Exteriors - Home">
              <img
                src={kodiakWhiteLogo}
                alt="Kodiak Structures & Exteriors logo"
                className="h-16 w-auto"
                width={240}
                height={64}
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="leading-relaxed text-footer-foreground/80">
              Expert roofing, siding, and exterior services built to withstand the toughest conditions. Serving homeowners throughout Minnesota.
            </p>
            <div className="space-y-2 text-sm text-footer-foreground/80">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-secondary" aria-hidden="true" />
                <a href="tel:6519831609" className="transition-colors hover:text-secondary">(651) 983-1609</a>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-secondary" aria-hidden="true" />
                <span>Mon–Fri 7AM–6PM | Sat–Sun Closed</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <nav aria-label="Services">
            <h2 className="mb-6 text-lg font-heading font-semibold text-primary-foreground">Our Services</h2>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link to={service.path} className="text-footer-foreground/80 transition-colors hover:text-secondary">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Quick Links */}
          <nav aria-label="Quick Links">
            <h2 className="mb-6 text-lg font-heading font-semibold text-primary-foreground">Quick Links</h2>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-footer-foreground/80 transition-colors hover:text-secondary">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Service Areas */}
          <div>
            <h2 className="mb-6 text-lg font-heading font-semibold text-primary-foreground">Service Areas</h2>
            <div className="space-y-2">
              <span className="block font-medium text-primary-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4 text-secondary" aria-hidden="true" />
                Throughout Minnesota
              </span>
              <ul className="space-y-1 pl-6 text-sm">
                {locations.map((loc) => (
                  <li key={loc.name}>
                    <Link to={loc.path} className="text-footer-foreground/80 hover:text-secondary transition-colors">{loc.name}, MN</Link>
                  </li>
                ))}
              </ul>
              <p className="pl-6 text-sm text-footer-foreground/60 mt-2">
                Plus service up to 2–3 hours from the Twin Cities
              </p>
              <Link to="/locations" className="inline-block mt-2 pl-6 text-sm text-secondary hover:text-secondary/80 transition-colors">
                View all service areas →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col items-center gap-4 text-center md:grid md:grid-cols-3 md:items-center md:gap-4 md:text-left">
            <p className="text-sm text-footer-foreground/60 md:text-left">
              © 2026 Kodiak Structures & Exteriors. All rights reserved.
            </p>
            <a
              href="https://skillmammoth.com/industries/roofing-siding"
              target="_blank"
              rel={skillMammothRel}
              className="inline-flex items-center justify-center gap-2 text-sm text-footer-foreground/60 transition-colors hover:text-secondary md:justify-self-center"
              aria-label="Site Design and SEO by Skill Mammoth Digital"
            >
              <img
                src={skillMammothLogo}
                alt="Skill Mammoth Digital logo"
                className="h-5 w-5"
                width={20}
                height={20}
                loading="lazy"
                decoding="async"
              />
              <span>Site Design & SEO by Skill Mammoth Digital</span>
            </a>
            <div className="flex gap-4 text-sm md:justify-self-end">
              <Link to="/privacy-policy" className="text-footer-foreground/60 hover:text-secondary transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-footer-foreground/60 hover:text-secondary transition-colors">Terms of Use</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
